import { Platform } from 'react-native';

import { GOOGLE_OAUTH_REDIRECT_SCHEME } from '../config/environment';
import {
  WEB_BILLING_CANCEL_PATH,
  WEB_BILLING_PORTAL_PATH,
  WEB_BILLING_SUCCESS_PATH,
} from '../core/navigation/webPaths';
import { apiRequest } from './apiClient';
import { openBillingBrowser, type BillingBrowserResult } from './openBillingBrowser';

export const PRO_PRICE_USD = 12.99;

/** App Store Connect reference name — shown on paywalls with length and price. */
export const PRO_SUBSCRIPTION_NAME = 'MoneyLens Pro Monthly';
export const PRO_SUBSCRIPTION_LENGTH = '1 month';
export const PRO_SUBSCRIPTION_META = `${PRO_SUBSCRIPTION_NAME} · ${PRO_SUBSCRIPTION_LENGTH} · $${PRO_PRICE_USD}`;

export type CheckoutSessionResponse = {
  url: string;
  sessionId: string;
  priceUsd?: number;
};

export type PortalSessionResponse = {
  url: string;
};

/** Set when Checkout/Portal opens so return listeners know to refresh. */
let billingReturnPending = false;

export function markBillingReturnPending(): void {
  billingReturnPending = true;
}

export function consumeBillingReturnPending(): boolean {
  if (!billingReturnPending) return false;
  billingReturnPending = false;
  return true;
}

function webOrigin(): string {
  // Prefer globalThis to avoid hard `window` dependency in RN tsc.
  const g = globalThis as { location?: { origin?: string } };
  if (g.location?.origin) return g.location.origin;
  return 'http://localhost:3000';
}

/** Success / cancel URLs Stripe redirects to after Checkout. */
export function billingReturnUrls(): { successUrl: string; cancelUrl: string } {
  if (Platform.OS === 'web') {
    const origin = webOrigin();
    // Land on the public thank-you page (Ads / Play “Add page” URL).
    return {
      successUrl: `${origin}${WEB_BILLING_SUCCESS_PATH}`,
      cancelUrl: `${origin}${WEB_BILLING_CANCEL_PATH}`,
    };
  }

  // Custom scheme — InAppBrowser.openAuth dismisses when Stripe hits this host.
  const scheme = GOOGLE_OAUTH_REDIRECT_SCHEME;
  return {
    successUrl: `${scheme}://billing/success`,
    cancelUrl: `${scheme}://billing/cancel`,
  };
}

export function portalReturnUrl(): string {
  if (Platform.OS === 'web') {
    return `${webOrigin()}${WEB_BILLING_PORTAL_PATH}`;
  }
  // Same host as Checkout so openAuth / Linking close the sheet.
  return `${GOOGLE_OAUTH_REDIRECT_SCHEME}://billing/portal-return`;
}

export async function createCheckoutSession(): Promise<CheckoutSessionResponse> {
  const urls = billingReturnUrls();
  return apiRequest<CheckoutSessionResponse>('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(urls),
  });
}

export async function createPortalSession(): Promise<PortalSessionResponse> {
  return apiRequest<PortalSessionResponse>('/billing/portal', {
    method: 'POST',
    body: JSON.stringify({ returnUrl: portalReturnUrl() }),
  });
}

/** Start Stripe Checkout — native sheet closes on deep-link return. iOS uses Apple IAP instead. */
export async function startProCheckout(): Promise<BillingBrowserResult> {
  if (Platform.OS === 'ios') {
    throw new Error('Pro on iOS is sold through the App Store');
  }
  const session = await createCheckoutSession();
  markBillingReturnPending();
  return openBillingBrowser(session.url);
}

/** Open Stripe Customer Portal — native sheet closes on deep-link return. iOS uses Apple subscriptions. */
export async function openBillingPortal(): Promise<BillingBrowserResult> {
  if (Platform.OS === 'ios') {
    throw new Error('Manage Pro on iOS in Apple ID subscriptions');
  }
  const session = await createPortalSession();
  markBillingReturnPending();
  return openBillingBrowser(session.url);
}
